import React, { useState } from "react";
import { useAccounting } from "../hooks/useAccounting";
import tw from "twin.macro";
import styled from "styled-components";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Footer from "components/footers/FourColumnWithInputForm.js";
import AccountingTable from "components/tables/AccountingTable.js";
import AccountingDetailsTable from "components/tables/AccountingDetailTable.js";
import AccountingAccordion from "components/accordions/AccountingAccordion.js";
import AccountingDetailsAccordion from "components/accordions/AccountingDetailsAccordion.js";
import Header from "./../components/headers/HeaderLight"
import { Row, Col, Button, ButtonGroup } from "reactstrap";
import { SectionHeading } from "components/misc/Headings.js";
import IllustrationSrc from "images/capital-flow.png";
import { css } from "styled-components/macro"; //eslint-disable-line
import FilterByDates from "components/misc/FilterByDates";
import moment from 'moment';

const Container = tw.div`relative`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto items-center`;
const ImageColumn = tw(Column)`md:w-6/12 flex-shrink-0 relative`;
const TextColumn = styled(Column)(props => [
    tw`md:w-6/12 mt-16 md:mt-0`,
    props.textOnLeft ? tw`md:mr-12 lg:mr-16 md:order-first` : tw`md:ml-12 lg:ml-16 md:order-last`
]);
const Image = styled.img(props => [
    props.imageRounded && tw`rounded`,
    props.imageBorder && tw`border`,
    props.imageShadow && tw`shadow`,
]);

const TextContent = tw.div`lg:py-8 text-center md:text-left`;
const Heading = tw(
    SectionHeading
)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`;

export default () => {
    const [title, setTitle] = useState("Balance General");
    const [rSelected, setRSelected] = useState(1);
    const [tableOption, setTableOption] = useState(1);
    const [selectedDateStart, setSelectedDateStart] = useState(null);
    const [selectedDateEnd, setSelectedDateEnd] = useState(null);
    const [filteredTableData, setFilteredTableData] = useState([]);
    const [filteredIncomeTableData, setFilteredIncomeTableData] = useState([]);
    const [filteredOutcomeTableData, setFilteredOutcomeTableData] = useState([]);

        // Use shared accounting hook
    const { operations } = useAccounting();

    // Map operations to tableData, incomeData, outcomeData
    const tableData = React.useMemo(() => {
        // Group by category and sum incomes/outcomes
        const grouped = {};
        operations.forEach(op => {
            if (!grouped[op.category]) grouped[op.category] = { category: op.category, income: 0, outcome: 0, date: op.date };
            if (op.type === "income") grouped[op.category].income += op.amount;
            if (op.type === "outcome") grouped[op.category].outcome += op.amount;
        });
        // Calculate balance for each category
        const arr = Object.values(grouped).map((g, i) => ({
            id: i + 1,
            category: g.category,
            income: g.income ? `$${g.income.toLocaleString()}` : "",
            outcome: g.outcome ? `$${g.outcome.toLocaleString()}` : "",
            balance: `$${(g.income - g.outcome).toLocaleString()}`,
            date: g.date
        }));
        // Optionally add a TOTAL row
        if (arr.length > 0) {
            const totalIncome = arr.reduce((sum, r) => sum + (parseFloat(r.income.replace(/[$,]/g, "")) || 0), 0);
            const totalOutcome = arr.reduce((sum, r) => sum + (parseFloat(r.outcome.replace(/[$,]/g, "")) || 0), 0);
            arr.push({
                id: arr.length + 1,
                category: "TOTAL:",
                income: `$${totalIncome.toLocaleString()}`,
                outcome: `$${totalOutcome.toLocaleString()}`,
                balance: `$${(totalIncome - totalOutcome).toLocaleString()}`,
                date: arr[0].date
            });
        }
        return arr;
    }, [operations]);

    const incomeData = React.useMemo(() =>
        operations.filter(op => op.type === "income").map(op => ({
            id: op.id,
            date: op.date,
            category: op.category,
            description: op.description,
            paymentType: op.reference || "-",
            total: `$${op.amount.toLocaleString()}`
        })),
        [operations]
    );

    const outcomeData = React.useMemo(() =>
        operations.filter(op => op.type === "outcome").map(op => ({
            id: op.id,
            date: op.date,
            category: op.category,
            description: op.description,
            paymentType: op.reference || "-",
            total: `$${op.amount.toLocaleString()}`
        })),
        [operations]
    );
    
    // Filter by selected date
    React.useEffect(() => {
        let filtered = [...tableData];
        let filteredIncome = [...incomeData];
        let filteredOutcome = [...outcomeData];

        if (selectedDateStart && selectedDateEnd) {
            filtered = filtered.filter(p => {
                const d = moment(p.date, 'YYYY-MM-DD');
                return d.isSameOrAfter(selectedDateStart, 'day') && d.isSameOrBefore(selectedDateEnd, 'day');
            });
            filteredIncome = filteredIncome.filter(p => {
                const d = moment(p.date, 'YYYY-MM-DD');
                return d.isSameOrAfter(selectedDateStart, 'day') && d.isSameOrBefore(selectedDateEnd, 'day');
            });
            filteredOutcome = filteredOutcome.filter(p => {
                const d = moment(p.date, 'YYYY-MM-DD');
                return d.isSameOrAfter(selectedDateStart, 'day') && d.isSameOrBefore(selectedDateEnd, 'day');
            });
        } else if (selectedDateStart) {
            filtered = filtered.filter(p => {
                const d = moment(p.date, 'YYYY-MM-DD');
                return d.isSameOrAfter(selectedDateStart, 'day');
            });
            filteredIncome = filteredIncome.filter(p => {
                const d = moment(p.date, 'YYYY-MM-DD');
                return d.isSameOrAfter(selectedDateStart, 'day');
            });
            filteredOutcome = filteredOutcome.filter(p => {
                const d = moment(p.date, 'YYYY-MM-DD');
                return d.isSameOrAfter(selectedDateStart, 'day');
            });
        } else if (selectedDateEnd) {
            filtered = filtered.filter(p => {
                const d = moment(p.date, 'YYYY-MM-DD');
                return d.isSameOrBefore(selectedDateEnd, 'day');
            });
            filteredIncome = filteredIncome.filter(p => {
                const d = moment(p.date, 'YYYY-MM-DD');
                return d.isSameOrBefore(selectedDateEnd, 'day');
            });
            filteredOutcome = filteredOutcome.filter(p => {
                const d = moment(p.date, 'YYYY-MM-DD');
                return d.isSameOrBefore(selectedDateEnd, 'day');
            });
        }
        setFilteredTableData(filtered);
        setFilteredIncomeTableData(filteredIncome);
        setFilteredOutcomeTableData(filteredOutcome);
    }, [selectedDateStart, selectedDateEnd, tableData, incomeData, outcomeData]);

    const handleTabChange = (tab) => {
        if (tab === 1) {
            setRSelected(1);
            setTableOption(1);
            setTitle("Balance General");
        }
        else if (tab === 2) {
            setRSelected(2);
            setTableOption(2);
            setTitle("Balance de Ingresos");
        }
        else {
            setRSelected(3);
            setTableOption(3);
            setTitle("Balance de Egresos");
        }
    }

    return (
        <AnimationRevealPage>
            <Header />
            {/* Title */}
            <Container>
                <TwoColumn>
                    <ImageColumn>
                        <Image css={null} src={IllustrationSrc} imageBorder={false} imageShadow={false} imageRounded={true} />
                    </ImageColumn>
                    <TextColumn textOnLeft={true}>
                        <TextContent>
                            {/* <Subheading>{subheading}</Subheading> */}
                            <Heading>{<>
                                Estado de cuenta <span className="text-primary-500">del fraccionamiento.</span>
                            </>}</Heading>
                            <Description>{"Consulta el balance general de ingresos y egresos del fraccionamiento. Usa los filtros para ver el detalle por categoría o rango de fechas."}</Description>
                        </TextContent>
                    </TextColumn>
                </TwoColumn>
            </Container>
            {/*Dates Filter*/}
            <Row>
                <Col lg="4">
                    <FilterByDates
                        selectedDateStart={selectedDateStart}
                        setSelectedDateStart={setSelectedDateStart}
                        selectedDateEnd={selectedDateEnd}
                        setSelectedDateEnd={setSelectedDateEnd}
                    />
                </Col>
            </Row>
            {/*Tab Filter*/}
            <Row>
                <Col lg="12" style={{ textAlign: "left" }}>
                    <ButtonGroup className="my-2" size="sm">
                        <Button outline active={rSelected === 1} onClick={() => handleTabChange(1)}>
                            Balance General
                        </Button>
                        <Button outline active={rSelected === 2} onClick={() => handleTabChange(2)}>
                            Detalle Ingresos
                        </Button>
                        <Button outline active={rSelected === 3} onClick={() => handleTabChange(3)}>
                            Detalle Egresos
                        </Button>
                    </ButtonGroup>
                </Col>
            </Row>

            {/* Desktop Table - Only show PaymentTable on md and up */}
            <div className="hidden md:block w-full">
                {
                    tableOption === 1 &&
                    <AccountingTable title={title} tableData={filteredTableData} />
                }
                {
                    tableOption === 2 &&
                    <AccountingDetailsTable title={title} tableData={filteredIncomeTableData} />
                }
                {
                    tableOption === 3 &&
                    <AccountingDetailsTable title={title} tableData={filteredOutcomeTableData} />
                }
            </div>

            {/* Mobile Accordions - hidden on md and up */}
            <div className="md:hidden px-2 py-4">
                {
                    tableOption === 1 &&
                    <AccountingAccordion title={title} tableData={filteredTableData} />
                }
                {
                    tableOption === 2 &&
                    <AccountingDetailsAccordion title={title} tableData={filteredIncomeTableData} />
                }
                {
                    tableOption === 3 &&
                    <AccountingDetailsAccordion title={title} tableData={filteredOutcomeTableData} />
                }
            </div>

            <Footer />
        </AnimationRevealPage>
    )
};
